import React, { lazy } from "react";
import strings from "@/lang/strings";
import { PermissionEnum } from "@/types/permissions";

// lazy imports
const Find = lazy(() => import("@/pages/auth/find"));
const Login = lazy(() => import("@/pages/auth/login"));
const LoginEmail = lazy(() => import("@/pages/auth/login_email"));
const Signup = lazy(() => import("@/pages/auth/signup"));

// 관리자 관리 ////////////////////////////////
const Admin = lazy(() => import("@/pages/0.admin/Admin"));

// 회원 관리 ////////////////////////////////
const Member = lazy(() => import("@/pages/1.member/member/Member"));
const MemberDetail = lazy(
  () => import("@/pages/1.member/member/Member_detail")
);

// 기관 관리 ////////////////////////////////
const Corp = lazy(() => import("@/pages/2.corp/corp/Corp"));
const CorpDetail = lazy(() => import("@/pages/2.corp/corp/Corp_detail"));
const CorpDetailEdit = lazy(
  () => import("@/pages/2.corp/corp/Corp_detail_edit")
);
const CorpClassList = lazy(
  () => import("@/pages/2.corp/corp_class/Corp_class_list")
);
const CorpTutorTab = lazy(
  () => import("@/pages/2.corp/corp_tutor/Corp_tutor_tab")
);
const CorpStudent = lazy(
  () => import("@/pages/2.corp/corp_student/Corp_student")
);

// 클래스 관리 ////////////////////////////////
const Class = lazy(() => import("@/pages/3.class/class/Class"));
const ClassDetail = lazy(() => import("@/pages/3.class/class/Class_detail"));

// 매출 관리 ////////////////////////////////
const BusinessInfo = lazy(
  () => import("@/pages/5.sales/businessInfo/BusinessInfo")
);
const BusinessInfoDetail = lazy(
  () => import("@/pages/5.sales/businessInfo/BusinessInfo_detail")
);
const BusinessInfoAdd = lazy(
  () => import("@/pages/5.sales/businessInfo/BusinessInfo_add")
);
const BusinessInfoEdit = lazy(
  () => import("@/pages/5.sales/businessInfo/BusinessInfo_edit")
);

// 포인트 관리 ////////////////////////////////
// 포인트 설정
const PointSettingEdit = lazy(
  () => import("@/pages/6.point/point_setting/Point_setting_edit")
);
// 포인트 상품
const PointSetting = lazy(
  () => import("@/pages/6.point/point_setting/Point_setting")
);
const PointProduct = lazy(
  () => import("@/pages/6.point/point_product/Point_product")
);
const PointProductDetail = lazy(
  () => import("@/pages/6.point/point_product/Point_product_detail")
);
const PointProductAdd = lazy(
  () => import("@/pages/6.point/point_product/Point_product_add")
);
const PointProductEdit = lazy(
  () => import("@/pages/6.point/point_product/Point_product_edit")
);
const PointProductDelete = lazy(
  () => import("@/pages/6.point/point_product/Point_product_delete")
);
// 포인트 운영내역
const PointOperationLogs = lazy(
  () => import("@/pages/6.point/point_operation_logs/Point_operation_logs")
);

const FreeCharge = lazy(
  () => import("@/pages/6.point/free_charge/Free_charge")
);

// 카리아카샵 ////////////////////////////////
const ShopPayment = lazy(() => import("@/pages/4.shop/payment/Shop_payment"));
const ShopPaymentDetail = lazy(
  () => import("@/pages/4.shop/payment/Shop_payment_detail")
);
const ShopOrderSheet = lazy(
  () => import("@/pages/4.shop/payment/Shop_order_sheet")
);
// 제휴사 관리
const PartnerList = lazy(() => import("@/pages/4.shop/partner/Partner_list"));
const PartnerDetail = lazy(
  () => import("@/pages/4.shop/partner/Partner_detail")
);
const PartnerCreate = lazy(
  () => import("@/pages/4.shop/partner/Partner_create")
);

// 서비스 운영 관리
// 문의
const Inquiry = lazy(() => import("@/pages/7.services/inquiry/Inquiry"));
const InquiryDetail = lazy(
  () => import("@/pages/7.services/inquiry/Inquiry_detail")
);

// 메시지 관리 ////////////////////////////////
// 발신번호 등록 신청 (알리고 신청서 다운로드)
const SenderRegistration = lazy(
  () => import("@/pages/8.message/sender_registration/Sender_registration")
);

export interface IMenu {
  path: string;
  title: string;
  iconActive?: React.ReactNode;
  iconInactive?: React.ReactNode;
  user_type?: string;
  private?: boolean;
  role?: string;
  permissions?: string[];
  children: {
    path: string;
    title: string;
    exact?: boolean;
    private?: boolean;
    role?: string;
    permissions?: string[];
    component: () => React.ReactNode;
    user_type?: string;
    containPath?: string[];
  }[];
}

export interface ISubMenu {
  path: string;
  title: string;
  exact?: boolean;
  private?: boolean;
  component: () => React.ReactNode;
  user_type?: string;
  role?: string;
  permissions?: string[];
  containPath?: string[];
}

// 사이드바에는 안 보이고, URL 접근해서 들어가는 페이지
export const ExtraRoutes: ISubMenu[] = [
  {
    path: "/login",
    title: strings.login.login,
    exact: true,
    private: false,
    component: () => <LoginEmail />,
  },
  {
    path: "/login",
    title: strings.login.login,
    exact: true,
    private: false,
    component: () => <LoginEmail />,
  },
  {
    path: "/signup/:step",
    title: strings.signup,
    exact: true,
    private: false,
    component: () => <Signup />,
  },
  {
    path: "/find/:step",
    title: strings.find,
    exact: true,
    private: false,
    component: () => <Find />,
  },

  // 회원 관리 ////////////////////////////////
  // 회원 상세
  {
    path: "/member/member-detail/:id",
    title: "회원 정보",
    exact: true,
    private: true,
    role: "ADMIN",
    permissions: [PermissionEnum.MEMBER_MANAGEMENT],
    containPath: ["/member/member-detail"],
    component: () => <MemberDetail />,
  },

  // 기관 관리 ////////////////////////////////
  // 기관 상세
  {
    path: "/corp/corp-detail/:id",
    title: "기관 정보",
    exact: true,
    private: true,
    role: "ADMIN",
    permissions: [PermissionEnum.CORP_MANAGEMENT],
    containPath: ["/corp/corp-detail"],
    component: () => <CorpDetail />,
  },
  {
    path: "/corp/corp-detail-edit/:id",
    title: "기관 정보 수정",
    exact: true,
    private: true,
    role: "ADMIN",
    permissions: [PermissionEnum.CORP_MANAGEMENT],
    containPath: ["/corp/corp-detail-edit"],
    component: () => <CorpDetailEdit />,
  },
  {
    path: "/corp/corp-detail/:id/class-list",
    title: "기관 클래스 목록",
    exact: true,
    private: true,
    role: "ADMIN",
    permissions: [PermissionEnum.CORP_MANAGEMENT],
    containPath: ["/corp/corp-detail/:id/class-list"],
    component: () => <CorpClassList />,
  },
  {
    path: "/corp/corp-detail/:id/tutor-list",
    title: "기관 선생님 목록",
    exact: true,
    private: true,
    role: "ADMIN",
    permissions: [PermissionEnum.CORP_MANAGEMENT],
    containPath: ["/corp/corp-detail/:id/tutor-list"],
    component: () => <CorpTutorTab />,
  },
  {
    path: "/corp/corp-detail/:id/student-list",
    title: "기관 학생 목록",
    exact: true,
    private: true,
    role: "ADMIN",
    permissions: [PermissionEnum.CORP_MANAGEMENT],
    containPath: ["/corp/corp-detail/:id/student-list"],
    component: () => <CorpStudent />,
  },

  // 클래스 관리 ////////////////////////////////
  // 클래스 상세
  {
    path: "/class/class-detail/:id",
    title: "클래스 정보",
    exact: true,
    private: true,
    role: "ADMIN",
    permissions: [PermissionEnum.CLASSROOM_MANAGEMENT],
    containPath: ["/class/class-detail/:id"],
    component: () => <ClassDetail />,
  },
  // 학습 관리 ////////////////////////////////
  // 학습 상세

  // 매출 관리 ////////////////////////////////
  // 사업장 정보 상세
  {
    path: "/sales/businessInfo/detail/:id",
    title: "사업장 정보 상세",
    exact: true,
    private: true,
    role: "ADMIN",
    permissions: [
      PermissionEnum.BUSINESS_REGISTRATION_FIRST_APPROVE,
      PermissionEnum.BUSINESS_REGISTRATION_SECOND_APPROVE,
    ],
    containPath: ["/sales/businessInfo/detail/:id"],
    component: () => <BusinessInfoDetail />,
  },
  {
    path: "/sales/businessInfo/add",
    title: "사업장 정보 추가",
    exact: true,
    private: true,
    role: "ADMIN",
    permissions: [
      PermissionEnum.BUSINESS_REGISTRATION_FIRST_APPROVE,
      PermissionEnum.BUSINESS_REGISTRATION_SECOND_APPROVE,
    ],
    containPath: ["/sales/businessInfo/add"],
    component: () => <BusinessInfoAdd />,
  },
  {
    path: "/sales/businessInfo/edit/:id",
    title: "사업장 정보 수정",
    exact: true,
    private: true,
    role: "ADMIN",
    permissions: [
      PermissionEnum.BUSINESS_REGISTRATION_FIRST_APPROVE,
      PermissionEnum.BUSINESS_REGISTRATION_SECOND_APPROVE,
    ],
    containPath: ["/sales/businessInfo/edit/:id"],
    component: () => <BusinessInfoEdit />,
  },
  // 포인트 관리 ////////////////////////////////
  // 포인트 설정
  {
    path: "/point/setting-edit",
    title: "포인트 설정",
    exact: true,
    private: true,
    role: "ADMIN",
    permissions: [PermissionEnum.POINT_MANAGEMENT],
    containPath: ["/point/setting-edit"],
    component: () => <PointSettingEdit />,
  },
  // 포인트 상품 상세
  {
    path: "/point/product-detail/:id",
    title: "포인트 상품 상세",
    exact: true,
    private: true,
    role: "ADMIN",
    permissions: [PermissionEnum.POINT_MANAGEMENT],
    containPath: ["/point/product-detail/:id"],
    component: () => <PointProductDetail />,
  },
  {
    path: "/point/product-add",
    title: "포인트 상품 추가",
    exact: true,
    private: true,
    role: "ADMIN",
    permissions: [PermissionEnum.POINT_MANAGEMENT],
    containPath: ["/point/product-add"],
    component: () => <PointProductAdd />,
  },
  {
    path: "/point/product-edit/:id",
    title: "포인트 상품 수정",
    exact: true,
    private: true,
    role: "ADMIN",
    permissions: [PermissionEnum.POINT_MANAGEMENT],
    containPath: ["/point/product-edit/:id"],
    component: () => <PointProductEdit />,
  },
  {
    path: "/point/product-delete",
    title: "포인트 상품 마감",
    exact: true,
    private: true,
    role: "ADMIN",
    permissions: [PermissionEnum.POINT_MANAGEMENT],
    containPath: ["/point/product-delete"],
    component: () => <PointProductDelete />,
  },

  // 카리아카샵 ////////////////////////////////
  // 결제 상세
  {
    path: "/shop/payment-detail/:id",
    title: "결제 상세",
    exact: true,
    private: true,
    role: "ADMIN",
    permissions: [PermissionEnum.SHOP_MANAGEMENT],
    containPath: ["/shop/payment-detail"],
    component: () => <ShopPaymentDetail />,
  },
  // 제휴사 상세/수정 — SUPER_ADMIN 전용
  // (SHOP_MANAGEMENT 권한자에게는 결제 내역만 보이고 제휴사 관리는 노출되지 않음)
  {
    path: "/shop/partner-detail/:id",
    title: "제휴사 상세",
    exact: true,
    private: true,
    role: "SUPER_ADMIN",
    containPath: ["/shop/partner-detail"],
    component: () => <PartnerDetail />,
  },
  // 제휴사 등록 — SUPER_ADMIN 전용
  {
    path: "/shop/partner-create",
    title: "제휴사 등록",
    exact: true,
    private: true,
    role: "SUPER_ADMIN",
    containPath: ["/shop/partner-create"],
    component: () => <PartnerCreate />,
  },

  // 서비스 운영 관리 ////////////////////////////////
  // 문의
  {
    path: "/services/inquiry/detail/:id",
    title: "문의 상세",
    exact: true,
    private: true,
    role: "ADMIN",
    permissions: [PermissionEnum.SERVICE_MANAGEMENT],
    containPath: ["/services/inquiry/detail/:id"],
    component: () => <InquiryDetail />,
  },
];

// 사이드바 메뉴에 보이는 항목
export const SidebarMenu: IMenu[] = [
  // 관리자 관리
  {
    path: "/admin",
    title: "관리자 관리",
    role: "SUPER_ADMIN",
    children: [
      {
        path: "/admin/admin-list",
        title: "관리자",
        exact: true,
        private: true,
        role: "SUPER_ADMIN",
        component: () => <Admin />,
        containPath: [
          "/admin/admin-list",
          "/admin/admin-detail/:id",
          "/admin/admin-edit/:id",
        ],
      },
    ],
  },

  // 회원 관리
  {
    path: "/member",
    title: strings.menu.member,
    role: "ADMIN",
    permissions: [PermissionEnum.MEMBER_MANAGEMENT],
    children: [
      {
        path: "/member/member-list",
        title: "회원",
        exact: true,
        private: true,
        role: "ADMIN",
        component: () => <Member />,
        containPath: ["/member/member-list", "/member/member-detail/:id"],
      },
    ],
  },

  // 기관 관리
  {
    path: "/corp",
    title: strings.menu.corp,
    role: "ADMIN",
    permissions: [PermissionEnum.CORP_MANAGEMENT],
    children: [
      {
        path: "/corp/corp-list",
        title: "기관",
        exact: true,
        private: true,
        role: "ADMIN",
        component: () => <Corp />,
        containPath: [
          "/corp/corp-list",
          "/corp/corp-detail/:id",
          "/corp/corp-detail/:id/class-list",
          "/corp/corp-detail/:id/tutor-list",
          "/corp/corp-detail/:id/student-list",
        ],
      },
    ],
  },

  // 클래스 관리
  {
    path: "/class",
    title: strings.menu.class,
    role: "ADMIN",
    permissions: [PermissionEnum.CLASSROOM_MANAGEMENT],
    children: [
      {
        path: "/class/class-list",
        title: "클래스",
        exact: true,
        private: true,
        role: "ADMIN",

        component: () => <Class />,
        containPath: ["/class/class-list", "/class/class-detail/:id"],
      },
    ],
  },

  // 매출 관리
  {
    path: "/sales",
    title: "매출 관리",
    role: "ADMIN",
    permissions: [
      PermissionEnum.BUSINESS_REGISTRATION_FIRST_APPROVE,
      PermissionEnum.BUSINESS_REGISTRATION_SECOND_APPROVE,
    ],
    children: [
      {
        path: "/sales/businessInfo",
        title: "사업장 정보",
        exact: true,
        private: true,
        role: "ADMIN",
        component: () => <BusinessInfo />,
        containPath: [
          "/sales/businessInfo",
          "/sales/businessInfo/add",
          "/sales/businessInfo/detail/:id",
          "/sales/businessInfo/edit/:id",
        ],
      },
    ],
  },

  // 포인트 관리
  {
    path: "/point",
    title: "포인트 관리",
    role: "ADMIN",
    permissions: [PermissionEnum.POINT_MANAGEMENT],
    children: [
      {
        path: "/point/setting",
        title: "포인트 설정",
        exact: true,
        private: true,
        role: "ADMIN",
        component: () => <PointSetting />,
        permissions: [PermissionEnum.POINT_MANAGEMENT],
        containPath: ["/point/setting", "/point/setting-edit"],
      },
      {
        path: "/point/product",
        title: "포인트 상품",
        exact: true,
        private: true,
        role: "ADMIN",
        permissions: [PermissionEnum.POINT_MANAGEMENT],
        component: () => <PointProduct />,
        containPath: [
          "/point/product",
          "/point/product-detail/:id",
          "/point/product-add",
          "/point/product-edit/:id",
          "/point/product-delete",
        ],
      },
      {
        path: "/point/operation-logs",
        title: "운영내역",
        exact: true,
        private: true,
        role: "ADMIN",
        permissions: [PermissionEnum.POINT_MANAGEMENT],
        component: () => <PointOperationLogs />,
        containPath: ["/point/operation-logs"],
      },
      {
        path: "/point/free-charge",
        title: "무료 충전",
        exact: true,
        private: true,
        role: "ADMIN",
        permissions: [PermissionEnum.POINT_MANAGEMENT],
        component: () => <FreeCharge />,
        containPath: ["/point/free-charge"],
      },
    ],
  },

  // 카리아카샵
  {
    path: "/shop",
    title: "카리아카샵",
    role: "ADMIN",
    permissions: [PermissionEnum.SHOP_MANAGEMENT],
    children: [
      {
        path: "/shop/payment",
        title: "결제 내역",
        exact: true,
        private: true,
        role: "ADMIN",
        permissions: [PermissionEnum.SHOP_MANAGEMENT],
        component: () => <ShopPayment />,
        containPath: ["/shop/payment", "/shop/payment-detail/:id"],
      },
      {
        // 학원별 통합 주문서 (발송박스 동봉용) — 하와이매쓰 요청
        path: "/shop/order-sheet",
        title: "주문서 출력",
        exact: true,
        private: true,
        role: "ADMIN",
        permissions: [PermissionEnum.SHOP_MANAGEMENT],
        component: () => <ShopOrderSheet />,
        containPath: ["/shop/order-sheet"],
      },
      {
        // 제휴사 관리는 SUPER_ADMIN 전용 — SHOP_MANAGEMENT 권한자에게는 노출되지 않음.
        path: "/shop/partner",
        title: "제휴사 관리",
        exact: true,
        private: true,
        role: "SUPER_ADMIN",
        component: () => <PartnerList />,
        containPath: [
          "/shop/partner",
          "/shop/partner-detail/:id",
          "/shop/partner-create",
        ],
      },
    ],
  },

  // 서비스 운영 관리
  {
    path: "/services",
    title: "서비스 운영 관리",
    role: "ADMIN",
    permissions: [PermissionEnum.SERVICE_MANAGEMENT],
    children: [
      {
        path: "/services/inquiry",
        title: "문의",
        exact: true,
        private: true,
        role: "ADMIN",
        permissions: [PermissionEnum.SERVICE_MANAGEMENT],
        component: () => <Inquiry />,
        containPath: ["/services/inquiry", "/services/inquiry/detail/:id"],
      },
    ],
  },

  // 메시지 관리 — CORP_MANAGEMENT 권한 (발신번호 등록 승인/알리고 신청서 운영)
  // 권한관리에서 CORP_MANAGEMENT 를 부여받은 관리자도 접근 가능. SUPER_ADMIN 은 항상 접근 가능.
  {
    path: "/message",
    title: "메시지 관리",
    role: "ADMIN",
    permissions: [PermissionEnum.CORP_MANAGEMENT],
    children: [
      {
        path: "/message/sender-registration",
        title: "발신번호 등록 신청",
        exact: true,
        private: true,
        role: "ADMIN",
        permissions: [PermissionEnum.CORP_MANAGEMENT],
        component: () => <SenderRegistration />,
        containPath: ["/message/sender-registration"],
      },
    ],
  },
];
